import { useEffect, useState } from 'react'
import { useSetHeroPosition } from '../api/hooks'

interface Props {
  annee: number
  current: number
  onClose: () => void
}

export default function HeroEditor({ annee, current, onClose }: Props) {
  const [val, setVal] = useState(current)
  const save = useSetHeroPosition(annee)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const handleSave = () => {
    save.mutate(val, { onSuccess: () => onClose() })
  }

  return (
    <>
      <div className="fixed inset-0 bg-ocean-900/40 z-50 anim-in" onClick={onClose} aria-hidden />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hero-editor-title"
        className="
          fixed z-50 bg-white shadow-2xl flex flex-col
          inset-x-0 bottom-0 max-h-[90vh] rounded-t-2xl
          sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
          sm:max-w-lg sm:w-full sm:max-h-[80vh] sm:rounded-2xl
          anim-in
        "
      >
        <header className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100 flex items-start justify-between gap-2 rounded-t-2xl bg-white shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-700/80">
              🖼️ Ajuster l'image
            </div>
            <h3 id="hero-editor-title" className="font-bold text-ocean-900 text-base sm:text-lg leading-tight">
              Repositionner la photo du hero
            </h3>
            <p className="text-[11px] text-ocean-700 mt-0.5">
              Fais glisser le curseur pour cadrer la photo comme tu le souhaites.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 -m-1 rounded-full bg-coral-50 hover:bg-coral-100 text-coral-600 text-xl leading-none flex items-center justify-center shrink-0"
            aria-label="Fermer"
          >×</button>
        </header>

        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto">
          {/* Preview */}
          <div className="rounded-xl overflow-hidden bg-ocean-100 relative" style={{ aspectRatio: '16/6' }}>
            <picture>
              <source media="(min-width: 1024px)" srcSet="/hero-large.jpg" />
              <source media="(min-width: 640px)" srcSet="/hero-medium.jpg" />
              <img
                src="/hero-small.jpg"
                alt="Aperçu du cadrage"
                className="w-full h-full object-cover"
                style={{ objectPosition: `center ${val}%` }}
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-b from-ocean-900/20 via-transparent to-ocean-900/30 pointer-events-none" />
            <div className="absolute bottom-1 right-2 text-white text-[10px] font-mono bg-black/40 px-1.5 rounded">
              {val}%
            </div>
          </div>

          {/* Slider */}
          <div>
            <div className="flex items-center justify-between text-xs text-ocean-700 mb-1.5 font-semibold">
              <span>↑ Haut</span>
              <span>↓ Bas</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={val}
              onChange={e => setVal(parseInt(e.target.value, 10))}
              className="w-full accent-coral-500 h-2"
              aria-label="Position verticale de l'image"
            />
            <div className="flex justify-between text-[10px] text-ocean-600 mt-1">
              <span>0 %</span>
              <span>50 %</span>
              <span>100 %</span>
            </div>
          </div>

          {/* Boutons de raccourci */}
          <div className="flex flex-wrap gap-1.5">
            {[15, 30, 45, 60, 75].map(v => (
              <button
                key={v}
                type="button"
                onClick={() => setVal(v)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                  val === v
                    ? 'bg-ocean-500 text-white'
                    : 'bg-ocean-50 text-ocean-700 hover:bg-ocean-100'
                }`}
              >
                {v} %
              </button>
            ))}
          </div>
        </div>

        <footer className="px-4 sm:px-5 py-3 border-t border-ocean-100 bg-ocean-50/30 shrink-0 flex items-center justify-between gap-2 rounded-b-2xl">
          <button type="button" onClick={onClose} className="btn-ghost btn-sm">
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={save.isPending || val === current}
            className="btn-primary"
          >
            {save.isPending ? '⏳ Enregistrement…' : '✓ Enregistrer'}
          </button>
        </footer>
      </div>
    </>
  )
}
